import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createTestLesson } from '../../services/lessonService';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import NotificationOptIn from '../../components/Portal/NotificationOptIn';
import PropertyPicker from '../../components/Portal/PropertyPicker';
import {
	catalogFor,
	DEFAULT_DASHBOARD_CARDS,
	normalizeDashboardCards,
	orderedDashboardCards,
} from '../../components/Portal/dashboardCards';
import DashboardCardGrid from '../../components/Portal/DashboardCardGrid';
import {
	subscribeToMyPrefs,
	saveMyPrefs,
} from '../../services/UserPrefsService';
import './EmployeeDashboard.css';

// The sidebar shell is the nav, so the dashboard is a light home — a welcome +
// a few quick links into the most-used daily tools.
//
// The six cards used to be a hardcoded array here. They now come from
// dashboardCards.js (derived from portalNav) and each user picks their own set,
// stored in userPrefs/{uid}.dashboard. Anthony's call, 2026-07-26: role-
// correctness was already shipped and could not help two co-owners with
// identical permissions who do different jobs. See that file's header.

const EmployeeDashboard = () => {
	const { currentUser, userProfile } = useAuth();
	const navigate = useNavigate();
	const [creatingLesson, setCreatingLesson] = useState(false);
	const isAdmin = userProfile?.role === 'admin';
	const viewerUid = currentUser?.uid;

	const catalog = useMemo(() => catalogFor(userProfile), [userProfile]);
	// Start on the defaults rather than empty: the subscription resolves a tick
	// later, and initialising to [] would flash an empty grid on every load —
	// indistinguishable from "you turned everything off".
	const [cards, setCards] = useState(() =>
		normalizeDashboardCards(null, catalog)
	);

	// True once this person has arranged their cards; until then the grid keeps
	// sidebar order (dashboardCards.orderedDashboardCards says why).
	const [ordered, setOrdered] = useState(false);
	const [arranging, setArranging] = useState(false);

	useEffect(() => {
		if (!viewerUid) return undefined;
		return subscribeToMyPrefs(viewerUid, (prefs) => {
			setCards(normalizeDashboardCards(prefs?.dashboard?.cards, catalog));
			setOrdered(prefs?.dashboard?.ordered === true);
		});
	}, [viewerUid, catalog]);

	// Optimistic, matching the board: local state moves first so a toggle feels
	// instant and the write follows. A failed write is swallowed on purpose —
	// the onSnapshot above is the source of truth and corrects the UI on the next
	// tick, and a dashboard layout preference is never worth an error dialog.
	//
	// `ordered` rides along on EVERY write: saveMyPrefs replaces the whole
	// `dashboard` map, so a write without it would quietly undo an arrangement.
	const saveDashboard = (nextCards, nextOrdered) => {
		setCards(nextCards);
		setOrdered(nextOrdered);
		if (viewerUid) {
			saveMyPrefs(viewerUid, 'dashboard', { cards: nextCards, ordered: nextOrdered }).catch(() => {});
		}
	};

	// Before anyone has arranged, the stored array is in tick order, which the
	// grid ignores; keep it that way so ticking a box does not reshuffle.
	const handleCardsChange = (next) => saveDashboard(next, ordered);

	// The grid hands back the full order of the cards it SHOWS, which is the
	// whole selection (normalizeDashboardCards already dropped anything this
	// viewer cannot have). From here on that order is the dashboard's.
	const handleReorder = (nextOrder) => saveDashboard(nextOrder, true);

	const shown = orderedDashboardCards(cards, catalog, ordered);

	// Create a test lesson for development (admin-only on the home now).
	// Attached to the studio's test student since 2026-09-04, so everything
	// keyed on a student — the warmup routine, carry-forward, the drawer —
	// works in it; a student-less record opened the dashboard and nothing else.
	const handleCreateTestLesson = async () => {
		setCreatingLesson(true);
		try {
			const lessonDoc = await createTestLesson({ uid: currentUser.uid });
			navigate(`/portal/work/lesson/${lessonDoc.id}`);
		} catch (error) {
			console.error('Error creating test lesson:', error);
			alert('Failed to create test lesson');
		} finally {
			setCreatingLesson(false);
		}
	};

	return (
		<div className='portal-home'>
			<TestModeBanner />

			<header className='portal-home__hero'>
				<span className='eyebrow'>Employee portal</span>
				<h1 className='brand-heading'>Welcome back</h1>
				{/* Was "Use the sidebar to navigate. Here are your most-used tools."
				    — which claimed a personalization the code does not do: the cards
				    below are a hardcoded array and nothing measures usage. Anthony's
				    line, in his own voice, and it has the advantage of being true. */}
				<p className='portal-home__sub'>
					We are ladies and gentle thems, serving ladies and gentle thems.
				</p>
			</header>

			<NotificationOptIn />

			<section className='portal-home__section'>
				<div className='portal-home__section-head'>
					<h2 className='portal-home__h2'>Jump back in</h2>
					{shown.length > 1 && (
						<button
							type='button'
							className={`btn-cyv-ghost portal-home__picker portal-home__arrange${arranging ? ' is-active' : ''}`}
							aria-pressed={arranging}
							onClick={() => setArranging((v) => !v)}>
							<i
								className={`fa-sharp fa-solid ${arranging ? 'fa-check' : 'fa-arrows-up-down-left-right'}`}
								aria-hidden='true'
							/>{' '}
							{arranging ? 'Done' : 'Arrange'}
						</button>
					)}
					<PropertyPicker
						catalog={catalog}
						defaults={DEFAULT_DASHBOARD_CARDS}
						selected={cards}
						onChange={handleCardsChange}
						title='Show on the dashboard'
						triggerLabel='Cards'
						triggerClassName='btn-cyv-ghost portal-home__picker'
					/>
				</div>
				{shown.length === 0 ? (
					// A deliberately empty dashboard is a legitimate choice (see
					// normalizeDashboardCards), so this is not an error state — it just
					// has to say where the cards went, or an empty grid reads as broken.
					<p className='portal-home__note'>
						No cards chosen. Use <strong>Cards</strong> above to pick the
						tools you want here, or keep using the sidebar.
					</p>
				) : (
					<>
						{arranging && (
							<p className='portal-home__note'>
								Drag a card to a new spot, or use Earlier and Later. Your order is
								saved as you go. Tap <strong>Done</strong> when finished.
							</p>
						)}
						<DashboardCardGrid cards={shown} arranging={arranging} onReorder={handleReorder} />
					</>
				)}
			</section>

			{isAdmin && (
				<section className='portal-home__section'>
					<h2 className='portal-home__h2'>Setup &amp; data</h2>
					<p className='portal-home__note'>
						One-time setup and data tools. Admin only.
					</p>
					<div className='portal-home__grid'>
						<Link
							to='/portal/work/admin'
							className='cyv-card cyv-card--link portal-home__card'
							style={{ '--accent': 'var(--grad-brand)' }}>
							<span className='accent-bar' />
							<span className='portal-home__card-icon'>
								<i className='fa-sharp fa-solid fa-shield-halved' />
							</span>
							<h3 className='portal-home__card-title'>Admin Panel</h3>
							<p className='portal-home__card-text'>
								Manage users, roles, and system settings.
							</p>
							<span className='portal-home__card-cta'>
								Open <i className='fa-sharp fa-solid fa-arrow-right' />
							</span>
						</Link>

						<div
							className='cyv-card portal-home__card'
							style={{ '--accent': 'var(--grad-quiet)' }}>
							<span className='accent-bar' />
							<span className='portal-home__card-icon'>
								<i className='fa-sharp fa-solid fa-flask' />
							</span>
							<h3 className='portal-home__card-title'>Create test lesson</h3>
							<p className='portal-home__card-text'>
								Spin up a test lesson to preview the lesson dashboard.
							</p>
							<button
								className='btn-cyv-ghost portal-home__action'
								disabled={creatingLesson}
								onClick={handleCreateTestLesson}>
								{creatingLesson ? 'Creating…' : 'Create test lesson'}
							</button>
						</div>
					</div>
				</section>
			)}
		</div>
	);
};

export default EmployeeDashboard;
